import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCustomerContacts } from "@/hooks/use-customer-contacts";
import type { NextStepInput } from "@/hooks/use-close-follow-up";
import { PRIORITY_LABELS, dueQuickPicks } from "@/lib/follow-up-calc";
import { FOLLOW_UP_CHANNEL_LABELS } from "@/lib/follow-up-labels";
import { osloDateString } from "@/lib/oslo-time";
import type { FollowUpChannel, FollowUpPriority } from "@/lib/db-types";
import { cn } from "@/lib/utils";

const NO_CONTACT = "none";
const NO_CHANNEL = "none";

interface NextStepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  defaultContactId?: string | null;
  defaultChannel?: FollowUpChannel | null;
  /** Forhåndsutfylt tittel; standard er «Følg opp <kunde>». */
  defaultTitle?: string | null;
  pending?: boolean;
  onSubmit: (next: NextStepInput) => void;
}

/** Neste steg etter e-post eller telefon. Oppfølgingen kan ikke lukkes uten. */
export default function NextStepDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  defaultContactId,
  defaultChannel,
  defaultTitle,
  pending,
  onSubmit,
}: NextStepDialogProps) {
  const { data: contacts = [] } = useCustomerContacts(open ? customerId : undefined);
  const activeContacts = useMemo(() => contacts.filter((c) => c.is_active && !c.anonymized_at), [contacts]);
  const picks = useMemo(() => dueQuickPicks(new Date()), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<FollowUpPriority>("this_week");
  const [channel, setChannel] = useState<string>(NO_CHANNEL);
  const [contactId, setContactId] = useState<string>(NO_CONTACT);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle ?? `Følg opp ${customerName}`);
    setDue(picks[0].date);
    setPriority("this_week");
    setChannel(defaultChannel ?? NO_CHANNEL);
    setContactId(defaultContactId ?? NO_CONTACT);
  }, [open, customerName, defaultChannel, defaultContactId, defaultTitle, picks]);

  const valid = title.trim().length > 0 && !!due;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Neste steg</DialogTitle>
          <DialogDescription>Hva skjer videre med {customerName}? Oppfølgingen lukkes når neste steg er lagret.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            onSubmit({
              title: title.trim(),
              due_date: due,
              priority,
              channel: channel === NO_CHANNEL ? null : (channel as FollowUpChannel),
              contact_id: contactId === NO_CONTACT ? null : contactId,
            });
          }}
        >
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="next-step-title">Tittel</Label>
              <Input id="next-step-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="next-step-due">Forfall</Label>
              <div className="flex flex-wrap items-center gap-2">
                {picks.map((p) => (
                  <Button
                    key={p.key}
                    type="button"
                    size="sm"
                    variant={due === p.date ? "default" : "outline"}
                    aria-pressed={due === p.date}
                    onClick={() => setDue(p.date)}
                  >
                    {p.label}
                  </Button>
                ))}
                <Input id="next-step-due" type="date" value={due} min={osloDateString(new Date())} onChange={(e) => setDue(e.target.value)} className={cn("w-auto")} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="next-step-priority">Prioritet</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as FollowUpPriority)}>
                  <SelectTrigger id="next-step-priority" aria-label="Prioritet">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PRIORITY_LABELS) as FollowUpPriority[]).map((p) => (
                      <SelectItem key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="next-step-channel">Kanal</Label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger id="next-step-channel" aria-label="Kanal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANNEL}>Ikke valgt</SelectItem>
                    {(Object.keys(FOLLOW_UP_CHANNEL_LABELS) as FollowUpChannel[]).map((c) => (
                      <SelectItem key={c} value={c}>
                        {FOLLOW_UP_CHANNEL_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="next-step-contact">Kontaktperson</Label>
              <Select value={contactId} onValueChange={setContactId}>
                <SelectTrigger id="next-step-contact" aria-label="Kontaktperson">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTACT}>Ikke valgt</SelectItem>
                  {activeContacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!valid || pending}>
              {pending ? "Lagrer ..." : "Lagre og lukk oppfølgingen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
